import React from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import "./SignInPage.scss";
import OrangeBtn from "../../Reuseable/OrangeBtn/OrangeBtn";
import PasswordInput from "../../Reuseable/PasswordInput/PasswordInput";
import FormInput from "../../Reuseable/FormInput/FormInput";
import { loginUser } from "../../store/auth";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import fullLogo from "../../assets/Images/fulllogo_.png";
import {
  BarChartOutlined,
  MessageOutlined,
  SolutionOutlined,
} from "@ant-design/icons";

const SignInSchema = Yup.object().shape({
  email: Yup.string()
    .email("Please enter a valid email address")
    .required("Email is required"),

  password: Yup.string()
    .required("Password is required")
    .min(8, "Password must be at least 8 characters"),
});

/* What the product does, so the panel beside the form is not dead space.
   Each line names something that exists in the app - portfolios and
   allocation, the dialer and template campaigns, PTP/callback tracking -
   rather than describing screens in the abstract. */
const HIGHLIGHTS = [
  {
    icon: <SolutionOutlined />,
    title: "Allocate and work every case",
    copy: "Portfolios split across telecallers and field agents, with each disposition logged.",
  },
  {
    icon: <MessageOutlined />,
    title: "Reach customers on any channel",
    copy: "Dialer campaigns alongside SMS and WhatsApp templates, run from one place.",
  },
  {
    icon: <BarChartOutlined />,
    title: "Watch recovery as it happens",
    copy: "PTPs, callbacks, targets and action-code analytics on live dashboards.",
  },
];

const SignInPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  return (
    <div className="signinPageMain">
      {/* ---- brand side ---- */}
      <aside className="signin__brand">
        <div className="signin__brand-inner">
          <img src={fullLogo} alt="Collecteaze" className="signin__logo" />

          <div className="signin__pitch">
            {/* names the job the platform does, in the first line - the old
                headline ("Every account, every agent, one view") could have
                belonged to any product, and "account" reads as a login
                account on this screen of all places */}
            <h1>Debt collections, from allocation to recovery.</h1>
            <p>
              One workspace for your portfolios, your callers and your field
              agents.
            </p>
          </div>

          <ul className="signin__highlights">
            {HIGHLIGHTS.map((item) => (
              <li key={item.title}>
                <span className="signin__highlight-icon">{item.icon}</span>
                <span className="signin__highlight-text">
                  <b>{item.title}</b>
                  <i>{item.copy}</i>
                </span>
              </li>
            ))}
          </ul>
        </div>
      </aside>

      {/* ---- form side ---- */}
      <main className="signin__panel">
        <div className="signin__card">
          {/* the brand panel is hidden on small screens, so the mark repeats
              here rather than leaving the form unbranded */}
          <img src={fullLogo} alt="Collecteaze" className="signin__card-logo" />

          <header className="signin__head">
            <h2>Welcome back</h2>
            <p>Sign in to continue to your workspace.</p>
          </header>

          <Formik
            initialValues={{ email: "", password: "" }}
            validationSchema={SignInSchema}
            onSubmit={async (values, { setSubmitting, setErrors }) => {
              try {
                const result = await dispatch(
                  loginUser({
                    email: values.email,
                    password: values.password,
                  })
                );

                if (result?.payload?.idToken) {
                  toast.success("Login successful");

                  if (result.payload.is_superuser) {
                    navigate("/agencies");
                  } else {
                    navigate("/dashboard");
                  }
                }
                else {
                  toast.error(result?.payload?.data?.detail);

                }
              } catch (error) {
                // setErrors({ password: "Invalid email or password" });
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({ handleSubmit, isSubmitting }) => (
              <form className="formWrapper" onSubmit={handleSubmit}>
                <div className="basicDetail">
                  {/* these were 24px <h2>s reading "Enter your E-mail"; a field
                      needs a label, not a heading */}
                  <div className="detailBox">
                    <label htmlFor="email">Email</label>
                    <FormInput
                      id="email"
                      name="email"
                      label="Email"
                      type="email"
                      placeholder="you@company.com"
                      autoComplete="email"
                    />
                  </div>
                  <div className="detailBox">
                    <label htmlFor="password">Password</label>
                    <PasswordInput
                      name="password"
                      label="Password"
                      type="password"
                    />
                  </div>
                </div>

                {/* the button is already the form's submit control, so the
                    duplicate onClick that also fired handleSubmit is gone;
                    `disabled` was not a prop it reads - `disable` is */}
                <OrangeBtn
                  loading={isSubmitting}
                  type="submit"
                  text="Sign In"
                  disable={isSubmitting}
                />
              </form>
            )}
          </Formik>
        </div>

        <p className="signin__foot">
          © {new Date().getFullYear()} Collecteaze
        </p>
      </main>

      <Toaster position="top-center" reverseOrder={false} />
    </div>
  );
};

export default SignInPage;
